/*
 * bridge-assistant.js
 *
 * Turns the Bridge Section search box into the Diagnox "Ask" assistant.
 * When AI mode is enabled (data-bridge-enabled="true" + an endpoint), submitting
 * the box calls the Cloudflare Worker and renders a short answer + product link
 * inline, instead of running a normal store search.
 *
 * If anything fails (no endpoint, network error, JS disabled), the form falls
 * back to its native action="/search" behavior — nothing is ever blocked.
 *
 * Scoped per section, idempotent, and re-binds on Shopify theme-editor reloads.
 */
(function () {
  "use strict";

  var REQUEST_TIMEOUT_MS = 20000;
  var MAX_LEN = 500;

  // --- Editable copy ---------------------------------------------------------
  // Small label shown above the recommended product.
  var PRODUCT_LABEL = "Recommended for you";
  // Fixed disclaimer shown under the answer (kept consistent, not model-generated).
  var DISCLAIMER_TEXT =
    "For general information only — not a substitute for professional medical advice.";

  function initSection(root) {
    if (!root || root.dataset.bridgeBound === "1") return;
    if (root.dataset.bridgeEnabled !== "true") return; // AI mode off -> native search

    var endpoint = (root.dataset.bridgeEndpoint || "").trim();
    if (!endpoint) return; // no endpoint -> native search

    var form = root.querySelector("[data-bridge-form]");
    var input = root.querySelector("[data-bridge-input]");
    var output = root.querySelector("[data-bridge-output]");
    if (!form || !input || !output) return;

    root.dataset.bridgeBound = "1";
    var busy = false;

    function ask(question) {
      question = (question || "").trim();
      if (busy || !question) return;
      if (question.length > MAX_LEN) question = question.slice(0, MAX_LEN);
      busy = true;
      showLoading(output);

      var controller = new AbortController();
      var timer = setTimeout(function () {
        controller.abort();
      }, REQUEST_TIMEOUT_MS);

      fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question: question }),
        signal: controller.signal,
      })
        .then(function (r) {
          if (!r.ok) throw new Error("bad status " + r.status);
          return r.json();
        })
        .then(function (data) {
          renderAnswer(output, data);
        })
        .catch(function () {
          renderError(output);
        })
        .finally(function () {
          clearTimeout(timer);
          busy = false;
        });
    }

    form.addEventListener("submit", function (e) {
      e.preventDefault();
      ask(input.value);
    });

    // Optional: pills feed the assistant instead of navigating.
    if (root.dataset.bridgePillsFeed === "true") {
      var pills = root.querySelectorAll("[data-bridge-pill]");
      Array.prototype.forEach.call(pills, function (pill) {
        if (pill.getAttribute("aria-disabled") === "true") return;
        pill.addEventListener("click", function (e) {
          e.preventDefault();
          var prompt = pill.getAttribute("data-bridge-prompt") || pill.textContent;
          input.value = (prompt || "").trim();
          ask(input.value);
          try {
            output.scrollIntoView({ behavior: "smooth", block: "nearest" });
          } catch (err) {
            /* older browsers: no smooth scroll, ignore */
          }
        });
      });
    }
  }

  // --- rendering (DOM-built; user/model text goes in via textContent) --------

  function clear(node) {
    while (node.firstChild) node.removeChild(node.firstChild);
    node.hidden = false;
  }

  function showLoading(output) {
    clear(output);
    output.classList.remove("bridge-assistant--emergency");
    var p = document.createElement("p");
    p.className = "bridge-assistant__loading";
    p.textContent = "Thinking…";
    output.appendChild(p);
  }

  function renderError(output) {
    clear(output);
    output.classList.remove("bridge-assistant--emergency");
    var p = document.createElement("p");
    p.className = "bridge-assistant__error";
    p.textContent = "Sorry, something went wrong. Please try again.";
    output.appendChild(p);
  }

  function renderAnswer(output, data) {
    clear(output);
    data = data || {};
    output.classList.toggle("bridge-assistant--emergency", !!data.is_emergency);

    var card = document.createElement("div");
    card.className = "bridge-assistant__card";

    if (data.answer) {
      var answer = document.createElement("p");
      answer.className = "bridge-assistant__answer";
      answer.textContent = data.answer;
      card.appendChild(answer);
    }

    var rec = data.recommendation;
    if (rec && rec.url && rec.cta) {
      if (PRODUCT_LABEL) {
        var recoLabel = document.createElement("p");
        recoLabel.className = "bridge-assistant__reco-label";
        recoLabel.textContent = PRODUCT_LABEL;
        card.appendChild(recoLabel);
      }

      var product = document.createElement("div");
      product.className = "bridge-assistant__product";

      // Image is fetched from Shopify's public product JSON after render.
      var img = document.createElement("img");
      img.className = "bridge-assistant__product-img";
      img.setAttribute("alt", rec.title || "");
      img.setAttribute("loading", "lazy");
      img.style.display = "none";
      product.appendChild(img);
      loadProductImage(img, rec.url);

      var body = document.createElement("div");
      body.className = "bridge-assistant__product-body";

      if (rec.title) {
        var title = document.createElement("span");
        title.className = "bridge-assistant__product-title";
        title.textContent = rec.title;
        body.appendChild(title);
      }

      var cta = document.createElement("a");
      cta.className = "bridge-assistant__cta";
      cta.setAttribute("href", rec.url);
      cta.textContent = rec.cta;
      var arrow = document.createElement("span");
      arrow.className = "bridge-assistant__cta-arrow";
      arrow.setAttribute("aria-hidden", "true");
      arrow.textContent = " →";
      cta.appendChild(arrow);
      body.appendChild(cta);

      product.appendChild(body);
      card.appendChild(product);
    }

    if (data.article && data.article.url && data.article.title) {
      card.appendChild(makeResourceLink("Related reading: ", data.article.title, data.article.url, false));
    }
    if (data.video && data.video.url && data.video.title) {
      card.appendChild(makeResourceLink("Watch: ", data.video.title, data.video.url, true));
    }

    if (DISCLAIMER_TEXT) {
      var disc = document.createElement("p");
      disc.className = "bridge-assistant__disclaimer";
      disc.textContent = DISCLAIMER_TEXT;
      card.appendChild(disc);
    }

    output.appendChild(card);
  }

  function makeResourceLink(prefix, title, url, external) {
    var p = document.createElement("p");
    p.className = "bridge-assistant__more";
    var label = document.createElement("span");
    label.className = "bridge-assistant__more-label";
    label.textContent = prefix;
    var a = document.createElement("a");
    a.className = "bridge-assistant__link";
    a.setAttribute("href", url);
    a.textContent = title;
    if (external) {
      a.setAttribute("target", "_blank");
      a.setAttribute("rel", "noopener");
    }
    p.appendChild(label);
    p.appendChild(a);
    return p;
  }

  // Fetch the product's main image from Shopify's public product JSON
  // (/products/<handle>.js). Same-origin on the store; fails quietly otherwise.
  function loadProductImage(imgEl, productUrl) {
    var jsUrl;
    try {
      var u = new URL(productUrl, window.location.href);
      jsUrl = u.origin + u.pathname.replace(/\/+$/, "") + ".js";
    } catch (e) {
      jsUrl = productUrl.replace(/\/+$/, "") + ".js";
    }
    fetch(jsUrl, { headers: { Accept: "application/json" } })
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .then(function (p) {
        if (!p) return;
        var src = p.featured_image || (p.images && p.images[0]);
        if (!src) return;
        if (src.indexOf("//") === 0) src = "https:" + src;
        src = src + (src.indexOf("?") >= 0 ? "&" : "?") + "width=240";
        imgEl.src = src;
        imgEl.style.display = "";
      })
      .catch(function () {
        /* no image — leave the card without one */
      });
  }

  // --- boot ------------------------------------------------------------------

  function initAll(scope) {
    var roots = (scope || document).querySelectorAll("[data-bridge-section]");
    Array.prototype.forEach.call(roots, initSection);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  // Shopify theme editor: re-bind when a section is added/reloaded.
  document.addEventListener("shopify:section:load", function (e) {
    initAll(e.target || document);
  });
})();
